// Point Defence Weapons - Laser and Gauss Batteries
export const POINT_DEFENCE_WEAPONS = {
  // Point Defence Laser Batteries
  pointDefenceLaserType1: {
    id: "pointDefenceLaserType1",
    name: "Type I Point Defence Laser Battery",
    techLevel: 10,
    intercept: "+2D",
    power: 10,
    weight: 20, // tons
    cost: 5, // MCr
    traits: ["Point Defence", "Laser"] as string[],
    description: "Automated laser turrets for intercepting missiles and torpedoes",
    mountType: "pointDefenseBattery",
    damage: "0", // Point defence weapons don't deal damage to ships
    range: "Close" as const,
  },
  pointDefenceLaserType2: {
    id: "pointDefenceLaserType2",
    name: "Type II Point Defence Laser Battery",
    techLevel: 12,
    intercept: "+4D",
    power: 20,
    weight: 20, // tons
    cost: 10, // MCr
    traits: ["Point Defence", "Laser"] as string[],
    description: "Improved automated laser turrets for intercepting missiles and torpedoes",
    mountType: "pointDefenseBattery",
    damage: "0",
    range: "Close" as const,
  },
  pointDefenceLaserType3: {
    id: "pointDefenceLaserType3",
    name: "Type III Point Defence Laser Battery",
    techLevel: 14,
    intercept: "+6D",
    power: 30,
    weight: 20, // tons
    cost: 20, // MCr
    traits: ["Point Defence", "Laser"] as string[],
    description: "Advanced automated laser turrets for intercepting missiles and torpedoes",
    mountType: "pointDefenseBattery",
    damage: "0",
    range: "Close" as const,
  },
  
  // Point Defence Gauss Batteries
  pointDefenceGaussType1: {
    id: "pointDefenceGaussType1",
    name: "Type I Point Defence Gauss Battery",
    techLevel: 10,
    intercept: "+2D",
    power: 5,
    weight: 20, // tons
    cost: 3, // MCr
    traits: ["Point Defence", "Gauss"] as string[],
    description: "Gauss-based point defence primarily against torpedoes. Equal protection against missiles/torpedoes with Thrust ≤10, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefenseBattery",
    damage: "0",
    range: "Close" as const,
    ammo: {
      type: "gaussAmmo",
      capacity: 12, // 12 rounds before reload
      tonsPerReload: 1, // 1 ton per canister
      costPerReload: 30000, // Cr30,000 per canister
      name: "Gauss Ammunition Canister",
      traits: ["Ammunition"],
    },
  },
  pointDefenceGaussType2: {
    id: "pointDefenceGaussType2",
    name: "Type II Point Defence Gauss Battery",
    techLevel: 12,
    intercept: "+4D",
    power: 15,
    weight: 20, // tons
    cost: 6, // MCr
    traits: ["Point Defence", "Gauss"] as string[],
    description: "Improved gauss-based point defence primarily against torpedoes. Equal protection against missiles/torpedoes with Thrust ≤10, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefenseBattery",
    damage: "0",
    range: "Close" as const,
    ammo: {
      type: "gaussAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 30000,
      name: "Gauss Ammunition Canister",
      traits: ["Ammunition"],
    },
  },
  pointDefenceGaussType3: {
    id: "pointDefenceGaussType3",
    name: "Type III Point Defence Gauss Battery",
    techLevel: 14,
    intercept: "+6D",
    power: 25,
    weight: 20, // tons
    cost: 10, // MCr
    traits: ["Point Defence", "Gauss"] as string[],
    description: "Advanced gauss-based point defence primarily against torpedoes. Equal protection against missiles/torpedoes with Thrust ≤10, DM-2 vs Thrust 12-14, DM-6 vs Thrust 15+",
    mountType: "pointDefenseBattery",
    damage: "0",
    range: "Close" as const,
    ammo: {
      type: "gaussAmmo",
      capacity: 12,
      tonsPerReload: 1,
      costPerReload: 30000,
      name: "Gauss Ammunition Canister",
      traits: ["Ammunition"],
    },
  },
} as const;

// Screen Systems - Meson, Nuclear Damper, Black Globe
export const SCREEN_SYSTEMS = {
  mesonScreen: {
    id: "mesonScreen",
    name: "Meson Screen",
    techLevel: 13,
    power: 30,
    weight: 10, // tons
    cost: 20, // MCr
    traits: ["Screen", "Meson"] as string[],
    description: "Blocks attacks from meson weapons by preventing meson decay. Reduces meson weapon damage by 2D x 10 and removes Radiation trait",
    effect: "2D x 10 damage reduction vs meson weapons, removes Radiation trait",
    mountType: "screen",
    damage: "0",
    range: "Close" as const,
  },
  nuclearDamper: {
    id: "nuclearDamper",
    name: "Nuclear Damper",
    techLevel: 12,
    power: 20,
    weight: 10, // tons
    cost: 10, // MCr
    traits: ["Screen", "Nuclear"] as string[],
    description: "Projects nodes and antinodes to enhance/degrade strong nuclear force, rendering nuclear warheads ineffective",
    effect: "Reduces fusion/nuclear weapon damage by 2D and removes Radiation trait. Against Destructive weapons, every 5 dampers reduce damage by 1DD",
    mountType: "screen",
    damage: "0",
    range: "Close" as const,
  },
  blackGlobeGenerator: {
    id: "blackGlobeGenerator",
    name: "Black Globe Generator",
    techLevel: 15,
    power: 30,
    weight: 50, // tons
    cost: 100, // MCr
    traits: ["Screen", "Black Globe", "Experimental"] as string[],
    description: "Energy-absorbing barrier that shunts attack energy into capacitors. Automatically absorbs all incoming attacks. Ship cannot manoeuvre, dodge, jump, use weapons or sensors while active",
    effect: "Absorbs all attacks automatically. Requires capacitor management. Cannot manoeuvre/use weapons/sensors while active",
    mountType: "screen",
    damage: "0",
    range: "Close" as const,
    // Black Globe specific properties
    capacitorAbsorption: 50, // damage per ton of capacitors
    capacitorCost: 3, // MCr per ton of additional capacitors
    flickerRates: [
      { rate: 1, attackThreshold: 6, thrustPenalty: -1, sensorPenalty: -1 },
      { rate: 2, attackThreshold: 5, thrustPenalty: -2, sensorPenalty: -2 },
      { rate: 3, attackThreshold: 4, thrustPenalty: -3, sensorPenalty: -3 },
      { rate: 4, attackThreshold: 3, thrustPenalty: -4, sensorPenalty: -4 },
      { rate: 5, attackThreshold: 2, thrustPenalty: -5, sensorPenalty: -5 },
      { rate: 6, attackThreshold: 0, thrustPenalty: 0, sensorPenalty: 0 }, // Always on
    ],
  },
} as const;

// Combined export for all defensive systems
export const DEFENSIVE_SYSTEMS = {
  ...POINT_DEFENCE_WEAPONS,
  ...SCREEN_SYSTEMS,
} as const;
